/* eslint-disable no-undef */
import { Alert, Grid } from '@mui/material'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import { post } from '../http/restservice'
import LoadingButton from '@mui/lab/LoadingButton'
import PostHogTracker from '../utils/PostHogTracker'
import ReactGA4 from 'react-ga4'
import { isValidPhoneNumber as matchIsValidPhoneNumber } from 'libphonenumber-js'
import { AuthFloatingInput, AuthFloatingPhoneInput } from '../Components/Auth/AuthFloatingInput'
import { useThemeMode } from '../Provider/ThemeContextProvider'

const SignUp = () => {
  const navigate = useNavigate()
  const { darkMode } = useThemeMode()
  const location = useLocation()
  const initialPhoneNumber = location.state?.phoneNumber ?? ''

  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [partnerUser, setPartnerUser] = useState(false)

  useEffect(() => {
    fbq('track', 'Sign Up - Page Viewed')
    PostHogTracker.pageViewed('Sign Up Page')
    ReactGA4.event({
      category: 'View',
      action: 'Sign Up - Page Viewed',
      value: 'Sign Up - Page Viewed',
    })
    var custom_data = {
      Custom_Event_Property_Key1: 'Sign Up - Page Viewed',
    }

    branch.logEvent('Sign Up - Page Viewed', custom_data, function (err) {
      console.log(err)
    })
    setMessage(location.state?.message)
  }, [])

  const initialValues = useMemo(
    () => ({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: initialPhoneNumber,
      companyName: '',
      termsConditions: true,
    }),
    [initialPhoneNumber],
  )

  const validationSchema = yup.object({
    firstName: yup.string().trim().required('First name is required'),
    lastName: yup.string().trim(),
    email: yup.string().trim().email('Enter a valid email').required('Email is required'),
    phoneNumber: yup
      .string()
      .required('Phone number is required')
      .test('is-valid-phone', 'Enter a valid phone number', (value) =>
        matchIsValidPhoneNumber(value ?? ''),
      ),
    companyName: yup.string(),
    termsConditions: yup.bool().oneOf([true]).required(),
  })

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: () => {},
  })

  const isFormValid =
    !partnerUser &&
    Boolean(formik.values.firstName?.trim()) &&
    Boolean(formik.values.email?.trim()) &&
    !formik.errors.firstName &&
    !formik.errors.lastName &&
    !formik.errors.email &&
    !formik.errors.phoneNumber &&
    matchIsValidPhoneNumber(formik.values.phoneNumber ?? '')

  const signUp = async () => {
    if (!isFormValid) return

    try {
      PostHogTracker.updateUserProfile({
        name: formik.values.firstName + ' ' + formik.values.lastName,
        phone_number: formik.values.phoneNumber,
        email: formik.values.email,
        first_name: formik.values.firstName,
        last_name: formik.values.lastName,
        email_notifications: true,
        push_notifications: true,
        sms_notifications: true,
        whatsapp_notifications: true,
      })
      let signUpRequest = formik.values
      // signUpRequest.email = signUpRequest.phoneNumber + '@fabits.com'
      const response = await post('/customerservice/api/customer/signup', signUpRequest)

      setLoading(false)
      if (response.isError) {
        setMessage(response.response)
        return
      } else if (response.isPartnerUser) {
        setMessage(response.message)
        setPartnerUser(true)
        window.open(response.redirectUrl, '_blank')
        return
      }
      navigate('/activate-account-otp', {
        state: {
          phoneNumber: formik.values.phoneNumber,
          message: '',
        },
      })
    } catch (e) {
      console.error('Error during sign-up:', e)
      setLoading(false)
    }
  }

  return (
    <div className='mx-auto flex w-full max-w-[360px] flex-col items-center justify-center lg:max-w-[432px]'>
      <div className='mb-6 flex w-full flex-col gap-y-1'>
        <h1 className='text-center font-IBM text-[28px] font-bold leading-9 text-black-950 lg:text-[36px] lg:leading-10 dark:text-[var(--fb-text)]'>
          Sign Up
        </h1>
        <p className='text-center font-IBM text-sm font-medium leading-5 text-black-400 dark:text-black-300'>
          Get started in a few easy steps
        </p>
      </div>
      <div className='w-full'>
        <Grid>
          {message && (
            <Alert
              severity='error'
              className='!flex !items-center !font-IBM !bg-error-50 !py-1 !rounded-[12px] !text-error-600 !font-medium !gap-x-0 !mb-6'
            >
              {message}
            </Alert>
          )}
        </Grid>
        <form onSubmit={formik.handleSubmit} className='flex flex-col gap-y-6 font-IBM'>
          <div className='flex gap-x-4'>
            <AuthFloatingInput
              id='firstName'
              label='First name'
              required
              darkSurface={darkMode}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={(e) => {
                formik.handleBlur(e)
                PostHogTracker.firstNameEntered('Sign Up')
                fbq('track', 'Sign Up - First Name Entered')
                ReactGA4.event({
                  category: 'View',
                  action: 'Sign Up - First Name Entered',
                  value: 'Sign Up - First Name Entered',
                })
                var custom_data = {
                  Custom_Event_Property_Key1: 'Sign Up - First Name Entered',
                }

                branch.logEvent('Sign Up - First Name Entered', custom_data, function (err) {
                  console.log(err)
                })
              }}
            />

            <AuthFloatingInput
              id='lastName'
              label='Last name'
              darkSurface={darkMode}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={(e) => {
                formik.handleBlur(e)
                PostHogTracker.lastNameEntered('Sign Up')
                fbq('track', 'Sign Up - Last Name Entered')
                ReactGA4.event({
                  category: 'View',
                  action: 'Sign Up - Last Name Entered',
                  value: 'Sign Up - Last Name Entered',
                })
                var custom_data = {
                  Custom_Event_Property_Key1: 'Sign Up - Last Name Entered',
                }

                branch.logEvent('Sign Up - Last Name Entered', custom_data, function (err) {
                  console.log(err)
                })
              }}
            />
          </div>

          <AuthFloatingPhoneInput
            label='Mobile number'
            required
            darkSurface={darkMode}
            phoneNumber={formik.values.phoneNumber}
            setPhoneNumber={(value) => {
              setPartnerUser(false)
              formik.setFieldValue('phoneNumber', value, true)
            }}
          />

          <AuthFloatingInput
            id='email'
            type='email'
            label='Email'
            required
            darkSurface={darkMode}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <div className='flex items-start gap-x-3 rounded-2xl border border-[#2f3354] bg-[#161833]/90 px-3.5 py-3.5 lg:border-[#D3DBEC] lg:bg-[#F5F7FC] dark:lg:border-[var(--fb-border)] dark:lg:bg-[var(--fb-surface-muted)]'>
            <div className='mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#4c4eff]'>
              <span className='font-IBM text-xs font-bold leading-none text-white'>i</span>
            </div>
            <div className='min-w-0'>
              <p className='font-IBM text-sm font-semibold leading-5 text-black-950 dark:text-[var(--fb-text)]'>
                NRE / NRO Accounts
              </p>
              <p className='mt-0.5 font-IBM text-xs font-medium leading-4 text-black-400 dark:text-black-300'>
                For opening an NRE or NRO account, please reach out to our support team.
              </p>
            </div>
          </div>

          <div className='w-full'>
            <LoadingButton
              variant='contained'
              type='submit'
              loading={loading}
              sx={{
                height: '40px',
                px: '12px',
                fontSize: '14px',
                lineHeight: '20px',
                fontWeight: '600',
                fontFamily: 'IBM Plex Sans',
                textTransform: 'none',
                borderRadius: '12px',
                width: '100%',
                boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
                '&:hover': {
                  backgroundColor: '#2818ff',
                  boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
                },
                '&:active': { backgroundColor: '#2818ff', boxShadow: 'none' },
                '&.Mui-disabled': {
                  backgroundColor: '#97aaff',
                  color: '#fff',
                  boxShadow: 'none',
                  opacity: 1,
                },
              }}
              disabled={!isFormValid}
              onClick={() => {
                if (!isFormValid) return
                setLoading(true)
                signUp()
                PostHogTracker.signupAttempted('phone')
                fbq('track', 'Sign Up - OTP Initiated')
                ReactGA4.event({
                  category: 'View',
                  action: 'Sign Up - OTP Initiated',
                  value: 'Sign Up - OTP Initiated',
                })
                var custom_data = {
                  Custom_Event_Property_Key1: 'Sign Up - OTP Initiated',
                }

                branch.logEvent('Sign Up - OTP Initiated', custom_data, function (err) {
                  console.log(err)
                })
              }}
            >
              Continue
            </LoadingButton>
          </div>

          <p className='flex items-center justify-center gap-x-1.5 text-center font-IBM text-xs font-medium leading-4 text-black-400'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
              <path
                d='M12 2.5 4.5 5.5v5.8c0 5.1 3.5 9.9 7.5 11.2 4-1.3 7.5-6.1 7.5-11.2V5.5L12 2.5z'
                fill='#4c4eff'
              />
              <path
                d='M12 10.2a1.6 1.6 0 0 0-1.6 1.6v.5h-.5a.9.9 0 0 0-.9.9v2.2c0 .5.4.9.9.9h4.2c.5 0 .9-.4.9-.9v-2.2a.9.9 0 0 0-.9-.9h-.5v-.5A1.6 1.6 0 0 0 12 10.2zm0 1.2c.22 0 .4.18.4.4v.5h-.8v-.5c0-.22.18-.4.4-.4z'
                fill='#fff'
              />
            </svg>
            Your information is encrypted and securely protected.
          </p>
        </form>

        <div className='mt-6 flex w-full items-center gap-x-3'>
          <div className='h-px flex-1 bg-[#2a2d45] lg:bg-black-200 dark:lg:bg-[var(--fb-border)]' />
          <span className='font-IBM text-xs font-semibold tracking-wide text-black-400'>OR</span>
          <div className='h-px flex-1 bg-[#2a2d45] lg:bg-black-200 dark:lg:bg-[var(--fb-border)]' />
        </div>

        <div className='mt-4 flex items-center justify-center gap-x-1.5'>
          <p className='font-IBM text-sm font-medium leading-5 text-black-400'>
            Already have an account?
          </p>
          <button
            type='button'
            className='inline-flex items-center font-IBM text-sm font-semibold leading-5 text-primary-500'
            onClick={() => {
              navigate('/login')
            }}
          >
            Log in →
          </button>
        </div>
      </div>
    </div>
  )
}

export default SignUp
